// Router, save/load and other persistence-related actions for the dashboard editor.
const dashboardLayoutEditorPersistence = {
    /**
     * Edit dashboard widget.
     *
     * @param {*} block
     */
    editDashboardWidget(block) {
        const widgetType = block?.type || block?.id || '';

        if (!widgetType) {
            return;
        }

        const rootRouteName = this.$route.matched?.[0]?.name || this.$route.name;
        const routeNameByOwnerType = {
            user: 'dashboard-widget-form-user',
            role: 'dashboard-widget-form-role',
            default: rootRouteName === 'dashboard-editor-default'
                ? 'dashboard-widget-form-default'
                : 'dashboard-widget-form',
        };
        const routeName = this.$route.name || '';
        const routePath = this.$route.path || '';
        const ownerType = routeName.includes('-user') || routePath.includes('/dashboard-editor/user/')
            ? 'user'
            : (routeName.includes('-role') || routePath.includes('/dashboard-editor/role/')
                ? 'role'
                : 'default');

        this.$router.push({
            name: routeNameByOwnerType[ownerType] || 'dashboard-widget-form',
            params: {
                widgetType,
                hashId: block.id ? block.id : 'new',
                referenceId: block.referenceId,
            },
            query: {
                ...this.$route.query,
            },
        });
    },
    /**
     * Edit dashboard block.
     *
     * @param {*} block
     */
    editDashboardBlock(block) {
        this.editDashboardWidget(block);
    },
    /**
     * Trigger data provider event.
     *
     * @param {*} eventName
     */
    async triggerDataProviderEvent(eventName) {
        const callback = this.dataProvider.getEventListeners()[eventName];

        if (callback) {
            return await callback(this.breakpoints, this.blocks, this.visibility);
        }
    },
    /**
     * Show conditions for breakpoint.
     *
     * @param {*} breakpoint
     */
    showConditionsForBreakpoint(breakpoint) {
        this.$router.push({ name: 'blueprint-condition-form', params: { breakpointId: breakpoint.id } });
    },
    /**
     * Save blueprint.
     */
    async saveBlueprint() {
        this.loading = true;
        const result = await this.triggerDataProviderEvent('save');
        await this.dataProvider.fetchInterfaceData();
        this.refreshKey++;

        this.breakpoints = this.dataProvider.getBreakpoints();
        this.blocks = this.dataProvider.getBlocks();
        this.grid = this.dataProvider.getGrid();
        this.visibility = this.dataProvider.getVisibility();
        this.dashboardTabs = this.dataProvider.normalizeDashboardTabs(this.dataProvider.dashboardTabs);
        this.ensureActiveDashboardTab();
        this.blueprintDataStartState = JSON.parse(JSON.stringify(this.blueprintData));
        this.$actionHandler.handle(result.todos, this);
        this.loading = false;
    },
    /**
     * Toggle guides.
     */
    toggleGuides() {
        this.showGuides = !this.showGuides;
    },
    /**
     * Toggle hidden elements.
     */
    toggleHiddenElements() {
        this.showHiddenElements = !this.showHiddenElements;
    },
    /**
     * Show feature message.
     *
     * @param {*} message
     */
    showFeatureMessage(message) {
        this.featureModalOpen = true;
        this.featureMessage = message ? message : this.$t('Deze feature wordt niet ondersteund');
    },
    /**
     * Close feature modal.
     */
    closeFeatureModal() {
        this.featureModalOpen = false;
        this.featureMessage = null;
    },
    /**
     * Save data object.
     */
    async saveDataObject() {
        this.loading = true;
        await this.fetchData();
        this.loading = false;
    },
    /**
     * Check show confirm.
     *
     * @param {*} to
     * @param {*} from
     */
    checkShowConfirm(to, from) {
        let versionChanged = false;
        let leaveDashboardEditor = false;
        const blueprintDataChanged = !this.$deepEqual(this.blueprintDataStartState, this.blueprintData);

        if (to) {
            versionChanged = to.query.versionId !== from.query.versionId;
            leaveDashboardEditor = !to.matched.some((route) => (route.name || '').startsWith('dashboard-editor') || (route.name || '').startsWith('dashboard-widget-form'));
        }

        return blueprintDataChanged && (versionChanged || leaveDashboardEditor);
    },
    /**
     * Change version.
     *
     * @param {*} $event
     */
    changeVersion($event) {
        this.$router.push({
            query: {
                ...this.$route.query,
                versionId: $event.versionId,
            },
        });
    },
    /**
     * Fetch data.
     */
    async fetchData() {
        this.loading = true;
        await this.dataProvider.fetchBlueprintData();
        await this.dataProvider.fetchInterfaceData();
        this.loading = false;

        this.breakpoints = this.dataProvider.getBreakpoints();
        this.blocks = this.dataProvider.getBlocks();
        this.visibility = this.dataProvider.getVisibility();
        this.dashboardTabs = this.dataProvider.normalizeDashboardTabs(this.dataProvider.dashboardTabs);
        this.ensureActiveDashboardTab();
        this.featureHandler = this.dataProvider.getFeatureHandler(this.showFeatureMessage);
        this.grid = this.dataProvider.getGrid();
        this.refreshKey++;
        this.widgetTypes = await this.dataProvider.getBlockTypes();
    },
};

export default dashboardLayoutEditorPersistence;
